/* eslint-disable max-len */
import React, { useState } from 'react';
import styled from 'styled-components';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { useSubmitter } from '../../platform/Hook';
import { Button, Grid, Select, Toggle } from '../../ui';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { PreferencesModel } from '../preferences/PreferencesModel';

const OptionInfo = styled.div`
    display: inline-block;
    padding-right: 2rem;
    margin-right: 2rem;

    ${Title} {
        margin-bottom: .5rem;
        font-size: 1rem;
    }
    ${Copy} {
        margin: 0;
        font-size: .875rem;
    }
`;

const OptionControl = styled.div`
    display: inline-block;
    width: 10rem;
    display: flex;
    align-items: center;

    ${Select.Frame} {
        width: 100%;
    }
`;

export const NotificationsView = () => {

    const api = useResolve(ApiClient);
    const preferences = useResolve(PreferencesModel);
    const [state, setState] = useState(preferences.getState());

    const [onSubmit, _working, result] = useSubmitter(async (patch: Partial<typeof state>) => {
        const before = Object.fromEntries(Object.keys(patch).map(x => [x, state[x]]));
        setState(x => ({ ...x, ...patch }));
        try {
            await api.exec(v2.me.preferences.update(patch));
        }
        catch (e) {
            setState(x => ({ ...x, ...before }));
            throw e;
        }
    }, [state]);

    const [onClear, clearing, clearResult] = useSubmitter(async () => {
        await api.exec(v2.notifications.clear());
    }, []);

    return <Flexer width="42">
        <Grid columns="auto 10rem" columnGap="1rem" rowGap="2rem">
            <OptionInfo>
                <Title>Show Unread Notifications </Title>
                <Copy>
                    Controls whether or not the little red count of unread notifications appears in the top right of the screen.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.notificationUnreads} onAction={x => onSubmit({ notificationUnreads: x })} />
            </OptionControl>
            <OptionInfo>
                <Title>Clear All Notifications</Title>
                <Copy>
                    Clears all your notifications from the system.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Button disabled={clearing} onAction={onClear}>Clear All</Button>
            </OptionControl>
            <OptionInfo>
                <Title>Assigned Action Email Notifications</Title>
                <Copy>
                    Controls whether or not you receive email notifications when you are assigned actions.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.notificationAssignment} onAction={x => onSubmit({ notificationAssignment: x })} />
            </OptionControl>
            <OptionInfo>
                <Title>Daily Action Email Notifications</Title>
                <Copy>
                    Controls whether or not you receive daily email notifications with your actions due today.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.notificationDaily} onAction={x => onSubmit({ notificationDaily: x })} />
            </OptionControl>
            <OptionInfo>
                <Title>Weekly Action Email Notifications</Title>
                <Copy>
                    Controls whether or not you receive weekly email notifications with your actions due this week.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.notificationWeekly} onAction={x => onSubmit({ notificationWeekly: x })} />
            </OptionControl>
            <OptionInfo>
                <Title>Mention Email Notifications</Title>
                <Copy>
                    Controls whether or not you receive email notifications when you are mentioned on a board.
                </Copy>
            </OptionInfo>
            <OptionControl>
                <Toggle value={state.notificationEmailMention} onAction={x => onSubmit({ notificationEmailMention: x })} />
            </OptionControl>
        </Grid>
        <Flexer margin="2 0">
            <ResultInfoForSubmitter result={result} onSuccess="Changes saved." />
            <ResultInfoForSubmitter result={clearResult} onSuccess="Notifications cleared." />
        </Flexer>
    </Flexer>;
};
